import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Search, Filter, Heart, Shield, Clock, Award,
  CheckCircle, Star, TrendingUp, Users, Phone,
  Home, Stethoscope, Syringe, Scissors, Activity,
  ChevronRight, AlertCircle, Zap, X, HelpCircle, MessageCircle
} from 'lucide-react';
import api from '../../../api/axios';
import Header from '../../common/Header';
import Footer from '../../common/Footer';

const ParamedicalList = () => {
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('popular');
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/paramedical-services/')
      .then(res => {
        const servicesData = Array.isArray(res.data) ? res.data : Array.isArray(res.data.data) ? res.data.data : [];
        setServices(servicesData);
        setFilteredServices(servicesData);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setServices([]);
        setFilteredServices([]);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    let filtered = [...services];

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(service =>
        service.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        service.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Sort
    if (sortBy === 'price-low') {
      filtered.sort((a, b) => parseFloat(a.offer_price || a.price) - parseFloat(b.offer_price || b.price));
    } else if (sortBy === 'price-high') {
      filtered.sort((a, b) => parseFloat(b.offer_price || b.price) - parseFloat(a.offer_price || a.price));
    } else if (sortBy === 'discount') {
      filtered.sort((a, b) => {
        const discountA = a.offer_price ? (1 - parseFloat(a.offer_price) / parseFloat(a.price)) * 100 : 0;
        const discountB = b.offer_price ? (1 - parseFloat(b.offer_price) / parseFloat(b.price)) * 100 : 0;
        return discountB - discountA;
      });
    }

    setFilteredServices(filtered);
  }, [searchQuery, sortBy, services]);

  const getServiceIcon = (title) => {
    const lowerTitle = title.toLowerCase();
    if (lowerTitle.includes('nursing')) return <Users className="w-6 h-6" />;
    if (lowerTitle.includes('dressing') || lowerTitle.includes('wound')) return <Activity className="w-6 h-6" />;
    if (lowerTitle.includes('stitch')) return <Scissors className="w-6 h-6" />;
    if (lowerTitle.includes('injection') || lowerTitle.includes('iv') || lowerTitle.includes('saline')) return <Syringe className="w-6 h-6" />;
    if (lowerTitle.includes('catheter')) return <Stethoscope className="w-6 h-6" />;
    return <Heart className="w-6 h-6" />;
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gradient-to-br from-teal-50 via-blue-50 to-cyan-50 flex items-center justify-center">
          <div className="text-center">
            <div className="relative">
              <div className="animate-spin rounded-full h-20 w-20 border-4 border-teal-200 border-t-teal-600 mx-auto"></div>
              <Heart className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-8 h-8 text-teal-600 animate-pulse" />
            </div>
            <p className="mt-6 text-gray-700 text-lg font-medium">Loading healthcare services...</p>
            <p className="mt-2 text-gray-500 text-sm">Please wait while we fetch the best care for you</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-blue-50 to-cyan-50">

        {/* Hero Section */}
        <div className="relative bg-gradient-to-r from-teal-600 via-cyan-600 to-blue-600 text-white overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute transform rotate-45 -left-20 -top-20 w-96 h-96 bg-white rounded-full"></div>
            <div className="absolute transform -rotate-12 right-0 top-40 w-72 h-72 bg-white rounded-full"></div>
            <div className="absolute transform rotate-180 left-1/3 bottom-0 w-64 h-64 bg-white rounded-full"></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="text-center max-w-4xl mx-auto">
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                <Shield className="w-4 h-4" />
                <span className="text-sm font-medium">Certified & Trusted Healthcare</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 leading-tight">
                Professional Home Care
                <span className="block text-teal-200">At Your Doorstep</span>
              </h1>

              <p className="text-lg md:text-xl text-teal-100 mb-8 max-w-2xl mx-auto">
                Expert paramedical services delivered by qualified professionals.
                Quality healthcare in the comfort of your home.
              </p>

              {/* Search Bar */}
              <div className="max-w-2xl mx-auto mb-8">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Search for nursing, dressing, injection services..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-14 py-4 rounded-2xl text-gray-900 shadow-2xl focus:outline-none focus:ring-4 focus:ring-teal-300 text-lg"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 p-2 rounded-full bg-gray-200 hover:bg-gray-300 text-gray-600 hover:text-gray-900 transition-all duration-200 flex items-center justify-center"
                      style={{ background: 'rgb(229, 231, 235)', width: 'auto', border: 'none' }}
                      aria-label="Clear search"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-4 md:gap-8 max-w-3xl mx-auto">
                <div className="text-center">
                  <div className="text-3xl md:text-4xl font-bold mb-1">500+</div>
                  <div className="text-sm md:text-base text-teal-100">Services Delivered</div>
                </div>
                <div className="text-center border-x border-white/30">
                  <div className="text-3xl md:text-4xl font-bold mb-1">4.8★</div>
                  <div className="text-sm md:text-base text-teal-100">Average Rating</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl md:text-4xl font-bold mb-1">24/7</div>
                  <div className="text-sm md:text-base text-teal-100">Available</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filter & Sort Bar */}
        <div className="bg-white border-b sticky top-0 z-10 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">
                  {filteredServices.length} Service{filteredServices.length !== 1 ? 's' : ''} Available
                </span>
                {searchQuery && (
                  <span className="text-xs bg-teal-100 text-teal-700 px-2 py-1 rounded-full">
                    for "{searchQuery}"
                  </span>
                )}
              </div>

              <div className="flex items-center gap-3">
                <Filter className="w-4 h-4 text-gray-500" />
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white text-sm"
                >
                  <option value="popular">Most Popular</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                  <option value="discount">Best Discount</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Services Grid */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {filteredServices.length === 0 ? (
            <div className="text-center py-20">
              <div className="bg-white rounded-2xl shadow-xl p-12 max-w-md mx-auto">
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Search className="w-12 h-12 text-gray-400" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-3">No Services Found</h3>
                <p className="text-gray-600 mb-6">
                  {searchQuery
                    ? `No results for "${searchQuery}". Try different keywords.`
                    : 'Please check back later for available paramedical services.'
                  }
                </p>
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                  >
                    Clear Search
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredServices.map((service) => {
                const discount = service.offer_price
                  ? Math.round((1 - parseFloat(service.offer_price) / parseFloat(service.price)) * 100)
                  : 0;

                return (
                  <div
                    key={service.id}
                    className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-teal-200 flex flex-col h-full"
                  >
                    {/* Image Container */}
                    <div className="relative h-56 overflow-hidden bg-gradient-to-br from-teal-50 to-blue-50">
                      {service.image ? (
                        <img
                          src={`https://www.homecureexpress.com${service.image}`}
                          alt={service.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextElementSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}

                      <div
                        className={`absolute inset-0 flex items-center justify-center ${service.image ? 'hidden' : 'flex'}`}
                      >
                        <div className="text-center">
                          <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
                            {getServiceIcon(service.title)}
                          </div>
                          <p className="text-sm font-medium text-teal-700">Healthcare Service</p>
                        </div>
                      </div>

                      {/* Discount Badge */}
                      {discount > 0 && (
                        <div className="absolute top-4 right-4 bg-gradient-to-r from-red-500 to-pink-500 text-white px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1">
                          <Zap className="w-3 h-3" />
                          <span className="text-sm font-bold">{discount}% OFF</span>
                        </div>
                      )}

                      {/* Service Icon Badge */}
                      <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm text-teal-600 p-2 rounded-lg shadow-lg">
                        {getServiceIcon(service.title)}
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 flex flex-col p-6">
                      {/* Title */}
                      <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors">
                        {service.title}
                      </h3>

                      {/* Description */}
                      <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-3 flex-1">
                        {service.description}
                      </p>

                      {/* Features */}
                      <div className="grid grid-cols-2 gap-2 mb-4">
                        <div className="flex items-center gap-1.5 text-xs text-gray-600">
                          <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                          <span>Certified Staff</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-gray-600">
                          <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                          <span>Home Service</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-gray-600">
                          <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                          <span>Same Day</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-gray-600">
                          <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                          <span>Safe & Hygienic</span>
                        </div>
                      </div>

                      {/* Pricing */}
                      <div className="border-t pt-4 mt-auto">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <div className="flex items-baseline gap-2">
                              <span className="text-3xl font-bold text-teal-600">
                                ₹{service.offer_price || service.price}
                              </span>
                              {service.offer_price && (
                                <span className="text-lg text-gray-400 line-through">
                                  ₹{service.price}
                                </span>
                              )}
                            </div>
                            {service.offer_price && (
                              <p className="text-xs text-green-600 font-medium mt-1">
                                You save ₹{(parseFloat(service.price) - parseFloat(service.offer_price)).toFixed(2)}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-1 text-yellow-500">
                            <Star className="w-4 h-4 fill-current" />
                            <span className="text-sm font-semibold text-gray-700">4.8</span>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-2">
                          <Link
                            to={`/paramedical/${service.id}`}
                            className="flex-1 bg-gradient-to-r from-teal-600 to-cyan-600 text-white font-semibold py-3 px-4 rounded-xl hover:from-teal-700 hover:to-cyan-700 transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-teal-200 hover:shadow-xl"
                          >
                            <span>Book Now</span>
                            <ChevronRight className="w-4 h-4" />
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Why Choose Us Section */}
        <div className="bg-white border-y">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Why Choose Our Home Care Services?
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Trusted by thousands of families for professional healthcare at home
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center group">
                <div className="w-20 h-20 bg-gradient-to-br from-teal-100 to-cyan-100 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <Shield className="w-10 h-10 text-teal-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2 text-lg">100% Safe & Secure</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  All our professionals are verified, trained, and follow strict safety protocols
                </p>
              </div>

              <div className="text-center group">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <Award className="w-10 h-10 text-blue-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2 text-lg">Certified Professionals</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Licensed and experienced paramedical staff with proven track records
                </p>
              </div>

              <div className="text-center group">
                <div className="w-20 h-20 bg-gradient-to-br from-green-100 to-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <Clock className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2 text-lg">24/7 Availability</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Book anytime, anywhere. Emergency services available round the clock
                </p>
              </div>

              <div className="text-center group">
                <div className="w-20 h-20 bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <TrendingUp className="w-10 h-10 text-purple-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2 text-lg">Affordable Pricing</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Transparent pricing with no hidden charges. Best value for quality care
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* How It Works Section */}
        <div className="bg-gradient-to-br from-teal-50 to-cyan-50 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                How It Works
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Get professional healthcare at your doorstep in 3 simple steps
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="relative">
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
                  <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-500 text-white rounded-2xl flex items-center justify-center text-2xl font-bold mb-4 shadow-lg">
                    1
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Choose Service</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Browse our range of professional home care services and select what you need
                  </p>
                </div>
                <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                  <ChevronRight className="w-8 h-8 text-teal-300" />
                </div>
              </div>

              <div className="relative">
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-500 text-white rounded-2xl flex items-center justify-center text-2xl font-bold mb-4 shadow-lg">
                    2
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Book Appointment</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Schedule your preferred date and time. Our team will confirm within minutes
                  </p>
                </div>
                <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                  <ChevronRight className="w-8 h-8 text-teal-300" />
                </div>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 text-white rounded-2xl flex items-center justify-center text-2xl font-bold mb-4 shadow-lg">
                  3
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Get Care at Home</h3>
                <p className="text-gray-600 leading-relaxed">
                  Our certified professional arrives at your location with all necessary equipment
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Emergency Contact Section */}
        <div className="bg-gradient-to-r from-red-600 to-pink-600 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-center md:text-left">
                <div className="flex items-center gap-3 justify-center md:justify-start mb-2">
                  <Phone className="w-6 h-6 animate-pulse" />
                  <h3 className="text-2xl font-bold">Need Emergency Care?</h3>
                </div>
                <p className="text-red-100">Our medical team is available 24/7 for urgent home care needs</p>
              </div>
              <a
                href="tel:+919876543210"
                className="bg-white text-red-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-red-50 transition-colors shadow-2xl flex items-center gap-2 group"
              >
                <Phone className="w-5 h-5 group-hover:animate-bounce" />
                <span>Call: +91 98765 43210</span>
              </a>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-gradient-to-br from-gray-50 to-blue-50 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full mb-4">
                <HelpCircle className="w-4 h-4" />
                <span className="text-sm font-medium">Got Questions?</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Everything you need to know about our home care services. Can't find your answer? Feel free to contact us.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              {/* Left Column - Image */}
              <div className="relative order-2 lg:order-1">
                <div className="sticky top-24">
                  <div className="relative bg-gradient-to-br from-teal-100 to-cyan-100 rounded-3xl p-8 shadow-2xl overflow-hidden">
                    {/* Decorative elements */}
                    <div className="absolute top-0 right-0 w-32 h-32 bg-teal-200 rounded-full -translate-y-16 translate-x-16 opacity-50"></div>
                    <div className="absolute bottom-0 left-0 w-40 h-40 bg-cyan-200 rounded-full translate-y-20 -translate-x-20 opacity-50"></div>

                    {/* Main illustration placeholder */}
                    <div className="relative z-10 bg-white rounded-2xl p-8 shadow-xl">
                      <div className="flex flex-col items-center justify-center text-center space-y-6">
                        {/* Doctor and Patient Icons */}
                        <div className="flex items-end gap-4 mb-4">
                          <div className="relative">
                            <div className="w-24 h-24 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-full flex items-center justify-center shadow-lg">
                              <Users className="w-12 h-12 text-white" />
                            </div>
                            <div className="absolute -top-2 -right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center border-4 border-white">
                              <CheckCircle className="w-4 h-4 text-white" />
                            </div>
                          </div>
                          <div className="relative">
                            <div className="w-32 h-32 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center shadow-xl">
                              <Stethoscope className="w-16 h-16 text-white" />
                            </div>
                            <div className="absolute top-0 -right-1 animate-bounce">
                              <MessageCircle className="w-8 h-8 text-teal-600 fill-white" />
                            </div>
                          </div>
                        </div>

                        <div className="space-y-3">
                          <h3 className="text-2xl font-bold text-gray-900">
                            Need Help?
                          </h3>
                          <p className="text-gray-600 leading-relaxed">
                            Our support team is here to answer all your questions about home healthcare services
                          </p>
                        </div>

                        {/* Contact Options */}
                        <div className="w-full space-y-3 pt-4">
                          <a
                            href="tel:+919876543210"
                            className="w-full flex items-center justify-center gap-3 bg-gradient-to-r from-teal-600 to-cyan-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-teal-700 hover:to-cyan-700 transition-all shadow-lg"
                          >
                            <Phone className="w-5 h-5" />
                            <span>Call: +91 98765 43210</span>
                          </a>
                          <a
                            href="https://wa.me/919876543210?text=Hi%2C%20I%20have%20a%20question%20about%20paramedical%20services"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full flex items-center justify-center gap-3 bg-white border-2 border-teal-600 text-teal-600 px-6 py-3 rounded-xl font-semibold hover:bg-teal-50 transition-all"
                          >
                            <MessageCircle className="w-5 h-5" />
                            <span>WhatsApp Chat</span>
                          </a>
                        </div>

                        {/* Stats */}
                        <div className="grid grid-cols-2 gap-4 w-full pt-4 border-t">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-teal-600">24/7</div>
                            <div className="text-xs text-gray-600">Available</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-teal-600">&lt;2min</div>
                            <div className="text-xs text-gray-600">Response Time</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Floating badges */}
                    <div className="absolute top-8 left-8 bg-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2 animate-pulse">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-sm font-medium text-gray-700">Online Now</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - FAQs */}
              <div className="space-y-4 order-1 lg:order-2">
                {[
                  {
                    q: "Are your healthcare professionals certified?",
                    a: "Yes, all our paramedical staff are licensed, certified, and have undergone rigorous background checks and training. We ensure every professional meets the highest standards of healthcare delivery."
                  },
                  {
                    q: "What areas do you serve?",
                    a: "We provide services across major cities and their surrounding areas. Check availability for your location during booking. We're constantly expanding to serve more areas."
                  },
                  {
                    q: "Can I book services for the same day?",
                    a: "Yes, we offer same-day service based on availability. For emergency cases, please call our 24/7 helpline at +91 98765 43210 for immediate assistance."
                  },
                  {
                    q: "What safety measures do you follow?",
                    a: "We follow strict hygiene protocols, use sterilized equipment, and our staff wears appropriate PPE. Patient safety is our top priority. All our professionals are trained in infection control."
                  },
                  {
                    q: "How do I pay for the services?",
                    a: "We accept multiple payment methods including cash, card, UPI, and digital wallets. Payment can be made before or after service completion based on your preference."
                  },
                  {
                    q: "Can I reschedule my appointment?",
                    a: "Yes, you can reschedule your appointment up to 2 hours before the scheduled time without any charges. Contact our support team for assistance."
                  }
                ].map((faq, index) => (
                  <details key={index} className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-lg transition-shadow">
                    <summary className="flex items-start justify-between cursor-pointer p-6 hover:bg-gray-50 transition-colors">
                      <div className="flex items-start gap-3 flex-1">
                        <div className="mt-1 flex-shrink-0">
                          <HelpCircle className="w-5 h-5 text-teal-600" />
                        </div>
                        <span className="font-semibold text-gray-900 text-left pr-4">{faq.q}</span>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400 group-open:rotate-90 transition-transform flex-shrink-0 mt-1" />
                    </summary>
                    <div className="px-6 pb-6 pl-14 text-gray-600 leading-relaxed">
                      {faq.a}
                    </div>
                  </details>
                ))}

                {/* Still have questions CTA */}
                <div className="bg-gradient-to-r from-teal-600 to-cyan-600 rounded-2xl p-8 text-white text-center shadow-xl mt-8">
                  <MessageCircle className="w-12 h-12 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold mb-2">Still Have Questions?</h3>
                  <p className="text-teal-100 mb-6">
                    Our friendly support team is ready to help you 24/7
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <a
                      href="tel:+919876543210"
                      className="bg-white text-teal-600 px-6 py-3 rounded-xl font-semibold hover:bg-teal-50 transition-colors inline-flex items-center justify-center gap-2"
                    >
                      <Phone className="w-5 h-5" />
                      <span>Call Us Now</span>
                    </a>
                    <a
                      href="https://wa.me/919876543210?text=Hi%2C%20I%20need%20help%20with%20home%20healthcare%20services"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-teal-700 text-white px-6 py-3 rounded-xl font-semibold hover:bg-teal-800 transition-colors inline-flex items-center justify-center gap-2"
                    >
                      <MessageCircle className="w-5 h-5" />
                      <span>WhatsApp Chat</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
      <Footer />
    </>
  );
};

export default ParamedicalList;
